import { OvFormInputReadOnly, OvPriceInput } from '@components'
import { KEYS, TOOLTIPS } from '@constants'
import { formatValueToCurrency, roundNumber } from '@helpers/formatCentToAmount.helper'
import { filterAvailableVatCodes } from 'src/helpers/vat/filterVatCodes.helper'
import { useStore } from '@hooks/useStore'
import { Add, Inventory } from '@mui/icons-material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Hidden,
  List,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import Grid2 from '@mui/material/Unstable_Grid2/Grid2'
import { ISingleDocument, ISingleProduct, IVatCode } from '@typings'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { OvFormInput } from 'src/components/core/ov-form-input/ov-form-input'
import { OvFormContext } from 'src/components/core/ov-form-provider/ov-form-provider'
import { OvForm } from 'src/components/core/ov-form/ov-form'
import { ProductPickerModal } from 'src/partials/modals/products/productPicker.modal'

interface ITaskTableColumns {
  label: string
  key: string
  align: 'left' | 'right'
}

export const UpdateQuotationForm = observer(() => {
  const [expanded, setExpanded] = useState<string | false>(KEYS.GENERAL)
  const [productPickerOpen, setProductPickerOpen] = useState(false)
  const form = useFormContext()
  const formContext = useContext(OvFormContext)
  const { client, company, quotation, shared } = useStore()
  const FormInput = OvFormInput<ISingleDocument>
  const { fields, update, append, remove } = useFieldArray({
    control: form.control,
    name: 'lines',
  })
  const {
    formState: { defaultValues },
    setValue,
  } = useFormContext<ISingleDocument>()

  // Watch all form lines to calculate totals
  const formLines = useWatch({
    control: form.control,
    name: 'lines',
    defaultValue: [],
  })

  // Watch selected client to filter VAT codes
  const selectedClientId = useWatch({
    control: form.control,
    name: 'client_id',
  })

  // Get selected client from store
  const selectedClient = useMemo(() => {
    return client.list.find(c => c.id === selectedClientId)
  }, [selectedClientId, client.list])

  // Filter available VAT codes based on company and client tax status
  const filteredVatCodes = useMemo(() => {
    return filterAvailableVatCodes(shared.current_vat_codes, company.current_company, selectedClient)
  }, [shared.current_vat_codes, company.current_company, selectedClient])

  // Helper to get VAT percentage
  const getVatPercentage = useCallback(
    (vatCodeId: number) => {
      return shared.current_vat_codes.find(({ id }) => id === vatCodeId)?.percentage || 0
    },
    [shared.current_vat_codes]
  )

  // Calculate totals from current form state
  const calculatedTotals = useMemo(() => {
    if (!formLines || formLines.length === 0) {
      return { total: 0, totalExVat: 0, vat: 0 }
    }

    return formLines.reduce(
      (acc: any, line: any) => {
        const qty = Number(line.quantity) || 0
        const prc = Number(line.price) || 0
        const lineTotal = qty * prc
        const vatPercentage = getVatPercentage(line.vat_code_id)
        const lineVat = vatPercentage ? roundNumber(lineTotal * vatPercentage) / 100 : 0

        return {
          totalExVat: roundNumber(acc.totalExVat + lineTotal),
          vat: roundNumber(acc.vat + lineVat),
          total: roundNumber(acc.total + lineTotal + lineVat),
        }
      },
      { total: 0, totalExVat: 0, vat: 0 }
    )
  }, [formLines, getVatPercentage])

  const handleAccordion = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  // Check if company is tax payable
  const isCompanyTaxPayable = company.current_company?.tax_payable ?? true

  const taskTableColumns: ITaskTableColumns[] = [
    {
      label: 'Taak',
      key: 'label',
      align: 'left',
    },
    {
      label: 'Prijs',
      key: 'price',
      align: 'right',
    },
    {
      label: 'Aantal',
      key: 'quantity',
      align: 'right',
    },
    {
      label: 'Subtotaal',
      key: 'subtotal',
      align: 'right',
    },
    ...(isCompanyTaxPayable
      ? [
          {
            label: 'BTW percentage',
            key: 'percentage',
            align: 'right' as const,
          },
          {
            label: 'Totaal BTW',
            key: 'totalVat',
            align: 'right' as const,
          },
          {
            label: 'Totaal',
            key: 'total',
            align: 'right' as const,
          },
        ]
      : []),
  ]

  const handleAddRow = () => {
    append({ label: '', quantity: 1, price: 0, vat_code: { percentage: 0 } })
  }

  const handleProductSelect = (product: ISingleProduct) => {
    // Get the default VAT code (first available VAT code from filtered list only)
    // For non-tax-payable companies, filteredVatCodes will be empty, so defaultVatCode will be undefined
    const defaultVatCode = filteredVatCodes.length > 0 ? filteredVatCodes[0] : undefined

    // Append a new line with product data
    // Only assign VAT code if company is tax payable
    append({
      label: product.name,
      price: product.unit_price || 0,
      quantity: 1,
      vat_code_id: company.current_company?.tax_payable
        ? (product.vat_code_id || defaultVatCode?.id)
        : null,
      vat_code: company.current_company?.tax_payable ? defaultVatCode : null,
    })

    // Close the modal
    setProductPickerOpen(false)
  }

  const renderTableData = useMemo(() => {
    return (
      <TableContainer sx={{ height: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {taskTableColumns.map(singleColumn => (
                <TableCell
                  key={singleColumn.key}
                  align={singleColumn.align}>
                  {singleColumn.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {fields?.map(({ id, quantity, vat_code, vat_code_id, price, label, ...rest }: any, index) => {
              const currentValues = useWatch({
                name: `lines.${index}`,
              })

              const currentVatPercentage = useMemo(
                () => filteredVatCodes.find((code: IVatCode) => code.id === currentValues.vat_code_id)?.percentage,
                [filteredVatCodes, currentValues.vat_code_id]
              )

              const totalExVat = useCallback(
                () => currentValues.quantity * currentValues.price,
                [currentValues.quantity, currentValues.price]
              )

              const totalVat = useCallback(
                () => (currentVatPercentage ? (totalExVat() / 100) * currentVatPercentage : 0),
                [currentVatPercentage, totalExVat]
              )
              const total = useCallback(() => {
                const total = totalExVat() + totalVat()
                return total
              }, [totalExVat, totalVat])

              return (
                <TableRow key={`${id}-${quantity}-${index}`}>
                  <TableCell>
                    {formContext.readOnly ? (
                      label
                    ) : (
                      <TextField
                        disabled={formContext.readOnly}
                        size="small"
                        {...form.register(`lines.${index}.label`)}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {formContext.readOnly ? (
                      formatValueToCurrency(price)
                    ) : (
                      <OvFormInput
                        size="small"
                        type="currency"
                        label=""
                        name={`lines.${index}.price`}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    style={{ flex: 0.5 }}
                    align="right">
                    {formContext.readOnly ? (
                      quantity
                    ) : (
                      <TextField
                        disabled={formContext.readOnly}
                        size="small"
                        {...form.register(`lines.${index}.quantity`, {
                          valueAsNumber: true,
                        })}
                        inputProps={{ type: 'number', min: 0 }}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    key={`${quantity}-1-${price}-${vat_code_id}`}
                    align="right">
                    {formatValueToCurrency(totalExVat())}
                  </TableCell>
                  {isCompanyTaxPayable && (
                    <>
                      <TableCell
                        key={`${quantity}-2-${price}-${vat_code_id}`}
                        align="right">
                        {formContext.readOnly ? (
                          `${vat_code?.percentage}%`
                        ) : (
                          <OvFormInput
                            size="small"
                            label=""
                            name={`lines.${index}.vat_code_id`}
                            type="select"
                            options={filteredVatCodes as IVatCode[]}
                            valueKey="id"
                            labelKey="percentage"
                            output="value"
                            renderOption={value =>
                              value.name === 'BTW Verlegd' ? 'BTW verlegd' : `${value.percentage}%`
                            }
                          />
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        key={`${quantity}-${price}-3-${vat_code_id}`}>{`${
                        formatValueToCurrency(totalVat()) ?? '-'
                      }`}</TableCell>
                      <TableCell
                        align="right"
                        key={`${quantity}-${price}-4-${vat_code_id}`}>
                        {formatValueToCurrency(total())}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        {!formContext.readOnly && (
          <Box
            display="flex"
            gap={1}
            mt={2}>
            <Tooltip title={TOOLTIPS.ADD_QUOTATION_LINE}>
              <Button
                variant="text"
                onClick={handleAddRow}
                endIcon={<Add />}>
                Regel toevoegen
              </Button>
            </Tooltip>
            <Tooltip title={TOOLTIPS.SELECT_FROM_CATALOG}>
              <Button
                variant="text"
                onClick={() => setProductPickerOpen(true)}
                endIcon={<Inventory />}>
                Kies uit catalogus
              </Button>
            </Tooltip>
          </Box>
        )}
      </TableContainer>
    )
  }, [fields, form.control, formContext, filteredVatCodes, formLines])

  return (
    <>
      <OvForm>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Accordion
            defaultExpanded
            expanded={expanded === KEYS.GENERAL}
            onChange={handleAccordion(KEYS.GENERAL)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
              <Typography variant="h4">Algemeen</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid2
                container
                spacing={2}>
                <Grid2
                  xs={12}
                  md={6}>
                  <Stack spacing={2}>
                    {formContext.readOnly ? (
                      <OvFormInputReadOnly
                        label="Klant"
                        value={defaultValues?.client?.name}
                      />
                    ) : (
                      <FormInput
                        name="client_id"
                        type="select"
                        label="Klant"
                        options={client.list}
                        valueKey="id"
                        labelKey="name"
                        output="value"
                      />
                    )}
                    {formContext.readOnly && (
                      <OvFormInputReadOnly label="Adress">
                        <List
                          dense
                          disablePadding>
                          <ListItemText primary={defaultValues?.client?.street} />
                          <ListItemText primary={defaultValues?.client?.postal_code} />
                          <ListItemText primary={defaultValues?.client?.city} />
                          <ListItemText primary={defaultValues?.client?.country_id} />
                        </List>
                      </OvFormInputReadOnly>
                    )}
                  </Stack>
                </Grid2>
                <Grid2
                  xs={12}
                  md={6}>
                  <Stack spacing={2}>
                    <FormInput
                      name="created_at"
                      type="date"
                      label="Datum"
                      defaultValue={new Date().toISOString()}
                    />
                    <FormInput
                      name="number"
                      label="Offerte referentie"
                      value={defaultValues?.number}
                    />
                  </Stack>
                </Grid2>
              </Grid2>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === KEYS.LINES}
            onChange={handleAccordion(KEYS.LINES)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
              <Typography variant="h4">Regels</Typography>
            </AccordionSummary>
            <AccordionDetails>{renderTableData}</AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === KEYS.TERMS}
            onChange={handleAccordion(KEYS.TERMS)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
              <Typography variant="h4">Voorwaarden</Typography>
            </AccordionSummary>
            <AccordionDetails>Voorwaarden</AccordionDetails>
          </Accordion>
        </div>
      </OvForm>
      <Card
        sx={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          mt: 2,
        }}>
        <CardContent>
          <Grid2
            container
            spacing={2}>
            <Grid2 xs={isCompanyTaxPayable ? 4 : 12}>
              <Typography>Subtotaal</Typography>
              <Typography
                color="primary"
                fontWeight="bold">
                {formatValueToCurrency(formContext.readOnly ? quotation.single.subtotal : calculatedTotals.totalExVat)}
              </Typography>
            </Grid2>
            {isCompanyTaxPayable && (
              <>
                <Grid2 xs={4}>
                  <Typography>BTW</Typography>
                  <Typography
                    color="primary"
                    fontWeight="bold">
                    {formatValueToCurrency(formContext.readOnly ? quotation.single.vat : calculatedTotals.vat)}
                  </Typography>
                </Grid2>
                <Grid2 xs={4}>
                  <Typography>Totaal</Typography>
                  <Typography
                    color="primary"
                    fontWeight="bold">
                    {formatValueToCurrency(formContext.readOnly ? quotation.single.total : calculatedTotals.total)}
                  </Typography>
                </Grid2>
              </>
            )}
          </Grid2>
        </CardContent>
      </Card>

      <ProductPickerModal
        open={productPickerOpen}
        onClose={() => setProductPickerOpen(false)}
        onSelect={handleProductSelect}
      />
    </>
  )
})
